import React, { useCallback, useEffect } from 'react';
import { connect } from 'umi';
import { Card, Dropdown, List, Button } from 'tea-component';
import { GameInfo, ModelState } from '@/pages/aigc/pages/Text2SQL/interfaces';
import { namespace } from '@/pages/aigc/pages/Text2SQL/model';
import defaultIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import ChatInput from '@/pages/aigc/pages/Text2SQL/components/Chat/ChatInput';
import MessageContainer from '@/pages/datalab/pages/BehaviorAnalysis/GptAnalysis/Chat/components/MessageContainer';
import './index.scss';
import { useAigcIntl } from '@/pages/aigc/utils/utils';
import SvgIcon from '@/pages/home/components/SvgIcon';

interface ChatProps extends ModelState {
  gameList: GameInfo[];
  currentGame: GameInfo;
  dispatch: Function;
}

// 业务游戏展示
function GameItem({ game, isEn }: { game: GameInfo; isEn: boolean; }) {
  return (
    <>
      <img
        src={game?.cover || defaultIcon}
        onError={(e: any) => {
          e.target.src = defaultIcon;
        }}
        alt={''}
      />
      {isEn ? game?.name_en : game?.name}
    </>
  );
}

// 聊天消息
function ChatMessages(props: any) {
  const { messages, formatMessage, loading, hasTables, hasSQLResult, sendMessage, } = props;
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.position === 'left' && lastMessage?.type == 'text') {
    if (hasTables && hasSQLResult) {
      // 有表格数据和sql结果时, 添加的文本
      lastMessage.component = (
        <Button type={'link'} onClick={() => sendMessage(formatMessage('sql_rebuild'))}>
          {formatMessage('sql_rebuild')}
        </Button>
      );
    } else if (hasTables && !hasSQLResult) {
      // 只有表格数据, 没有sql数据的时候
      lastMessage.component = (
        <>
          <Button type={'link'} onClick={() => sendMessage(formatMessage('generate_sql'))}>
            {formatMessage('generate_sql')}
          </Button>
          {/* <Button type={'link'} onClick={() => sendMessage(formatMessage('generate_table'), true)}>*/}
          {/*  {formatMessage('generate_table')}*/}
          {/* </Button>*/}
        </>
      );
    }
  }

  const isGrammaticalMistake = window.sessionStorage.getItem('isGrammaticalMistake');
  return (
    <MessageContainer
      messages={messages}
      showThinking={loading}
      thinkingContent={
        <>
          <span className={'tea-action-state'}>
            <i className={'tea-mr-2n tea-icon tea-icon-loading'} />
            {formatMessage(isGrammaticalMistake && JSON.parse(isGrammaticalMistake) ? 'syntax_issue' : hasTables ? 'sql_loading' : 'chat_loading')}
          </span>
        </>
      }
    />
  );
}

function Chat(props: ChatProps) {
  const { gameList = [], currentGame, dispatch, messages, chatLoading, hasTables, hasSQLResult } = props;
  const { formatMessage: text2sqlFormat, isEn } = useAigcIntl('text2sql');
  const resetState = useCallback((game: GameInfo) => {
    dispatch({
      type: `${namespace}/resetState`,
      payload: {
        currentGame: game,
        messages: [
          {
            type: 'text',
            position: 'left',
            content: text2sqlFormat('first_message'),
            status: 'end',
            component: (
              <ul>
                <li
                  className={'message-shortcut'}
                  onClick={() => {
                    sendMessage(text2sqlFormat('question_one'));
                  }}
                >
                  {text2sqlFormat('question_one')}
                </li>
                <li
                  className={'message-shortcut'}
                  onClick={() => {
                    sendMessage(text2sqlFormat('question_two'));
                  }}
                >
                  {text2sqlFormat('question_two')}
                </li>
              </ul>
            ),
          },
        ],
      },
    });
  }, []);
  const sendMessage = useCallback(
    (value, isReset?) => {
      dispatch({
        type: `${namespace}/rebuildTableOrSql`,
        payload: {
          message: value,
          isEn,
          isReset,
        },
      });
    },
    [isEn],
  );

  useEffect(() => {
    gameList[0] && resetState(gameList[0]);
  }, [gameList]);

  return (
    <Card className={'chat-card'}>
      <Card.Header>
        <Dropdown
          trigger="click"
          clickClose={false}
          style={{ marginRight: 10 }}
          button={<GameItem game={currentGame} isEn={isEn} />}
        >
          <List type={'option'} className={'chat-header-game-list'}>
            {gameList?.map((game) => (
              <List.Item
                key={game?.game_code}
                className={game?.game_code === currentGame?.game_code ? 'selected' : ''}
                onClick={() => {
                  resetState(game);
                }}
              >
                <GameItem game={game} isEn={isEn} />
              </List.Item>
            ))}
          </List>
        </Dropdown>
      </Card.Header>
      <Card.Body className={'dbl-chat'}>
        <ChatMessages
          messages={messages}
          loading={chatLoading}
          formatMessage={text2sqlFormat}
          hasTables={hasTables}
          hasSQLResult={hasSQLResult}
          sendMessage={sendMessage}
        />
        {messages.length > 1 && !chatLoading ? (
          <Button
            className={'message-btn'}
            onClick={() => {
              resetState(currentGame);
            }}
          >
            <SvgIcon type={'datalab-clear'} />
            {text2sqlFormat('chat_clear')}
          </Button>
        ) : (
          ''
        )}
        {chatLoading ? (
          <Button
            className={'message-btn'}
            onClick={() => {
              window.sessionStorage.setItem('isCancel', JSON.stringify(true));
              dispatch({ type: `${namespace}/cancelFetchChat`, payload: { isEn } });
            }}
          >
            <SvgIcon type={'aigc-stop'} className={'aigc-stop'} />
            {text2sqlFormat('chat_stop')}
          </Button>
        ) : (
          ''
        )}
      </Card.Body>
      <Card.Footer>
        <ChatInput
          showStop={chatLoading}
          onSend={(value: string) => {
            sendMessage(value);
          }}
        />
      </Card.Footer>
    </Card>
  );
}

export default connect((state) => {
  const { currentGame, messages, chatLoading, relevantTables, sqlInfo, isGrammaticalMistake } = state[namespace];
  return {
    currentGame,
    messages,
    chatLoading,
    hasTables: !!relevantTables,
    hasSQLResult: !!sqlInfo,
    isGrammaticalMistake
  };
})(Chat);
